import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, X, AlertCircle, TrendingUp, TrendingDown, Target, Sparkles } from 'lucide-react';
import SectionActions from '@/components/SectionActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ExecutiveSummaryBrief — the rendered brief itself (no wrapping
 * chrome). Reused by both surface variants:
 *
 *  • `<ExecutiveSummaryModal>`  — popup variant for the standalone
 *    /triangle/:ticker page.
 *  • `<ExecutiveSummaryTab>`    — full-tab takeover for the Stock
 *    Modal (Triangle tab). Renders an absolute-positioned overlay
 *    with a Close X in the top-right corner that hides every other
 *    tab while open.
 *
 * Round 95ah (Jun 2026).
 */
export function ExecutiveSummaryBrief({ ticker, onClose, variant = 'modal' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const exportRef = useRef(null);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios
      .get(`${API}/stocks/${ticker}/executive-summary`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) {
          setData(r?.data || null);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e?.message || 'Failed to load executive summary');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [ticker]);

  return (
    <div className="bg-background" data-testid="executive-summary-brief">
      {/* Header — actions + close */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Sparkles className="w-4 h-4 text-violet-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              DiviDrip Executive Summary
            </div>
            <div className="text-sm font-bold truncate">
              {ticker} — Bull / Bear Thesis Starter
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <SectionActions
              targetRef={exportRef}
              elementId={`exec-summary-${ticker}`}
              title={`${ticker} · DiviDrip Executive Summary`}
              filename={`${ticker}-executive-summary`}
              testid="exec-summary-actions"
            />
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-muted/60 transition-colors"
              aria-label="Close executive summary"
              data-testid="exec-summary-close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div ref={exportRef} id={`exec-summary-${ticker}`} data-pdf-root className="px-4 py-4 space-y-4">
        {loading && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className="text-sm">Generating your brief…</span>
          </div>
        )}

        {err && !loading && (
          <div className="flex items-start gap-2 rounded-md border border-rose-300/60 bg-rose-50/60 dark:bg-rose-950/30 px-3 py-2 text-xs">
            <AlertCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
            <span>{err}</span>
          </div>
        )}

        {data && !loading && (
          <>
            {/* PDF/Print header */}
            <div className="pb-2 border-b border-border/60">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-base font-bold tracking-tight">
                  {ticker} · DiviDrip Executive Summary
                </div>
                <div className="text-[11px] font-mono text-muted-foreground">
                  {data.generated_at ? `Generated ${new Date(data.generated_at).toLocaleString()}` : ''}
                </div>
              </div>
              {data.headline && (
                <p className="mt-1.5 text-xs font-bold text-foreground/90 leading-snug">
                  {data.headline}
                </p>
              )}
              <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11px] font-bold">
                <span className="px-2 py-0.5 rounded-full bg-muted/60 border border-border">
                  Score: {data.score ?? '—'}/100
                </span>
                <span className="px-2 py-0.5 rounded-full bg-muted/60 border border-border">
                  Checklist: {data.checks_passed}/{data.checks_total}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-muted/60 border border-border">
                  {data.verdict_label}
                </span>
              </div>
            </div>

            {/* Executive Summary paragraph */}
            <section data-testid="exec-summary-section-executive">
              <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                Executive Summary
              </h3>
              <p className="text-xs font-bold leading-relaxed whitespace-pre-line">
                {data.sections?.executive_summary || ''}
              </p>
            </section>

            {/* Checklist analysis */}
            {Array.isArray(data.sections?.checklist_analysis) && data.sections.checklist_analysis.length > 0 && (
              <section data-testid="exec-summary-section-checklist">
                <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                  Buy Checklist Analysis
                </h3>
                <ul className="space-y-1 text-xs font-bold">
                  {data.sections.checklist_analysis.map((b, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="text-violet-500 mt-1">•</span>
                      <span className="leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* ASCII ladder — monospace block */}
            {data.ascii_ladder && (
              <section data-testid="exec-summary-section-ladder">
                <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                  Bull vs Bear at a Glance
                </h3>
                <pre className="text-[10px] sm:text-[11px] font-mono font-bold bg-muted/30 rounded-md p-3 leading-tight overflow-x-auto whitespace-pre">
                  {data.ascii_ladder}
                </pre>
              </section>
            )}

            {/* Bull + Bear side-by-side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Array.isArray(data.sections?.bull_case) && (
                <section
                  className="rounded-md border border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-950/30 p-2.5"
                  data-testid="exec-summary-section-bull"
                >
                  <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5" /> Bull Case
                  </h3>
                  <ul className="space-y-1 text-xs font-bold">
                    {data.sections.bull_case.map((b, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-emerald-600 dark:text-emerald-400 mt-1">▲</span>
                        <span className="leading-snug">{b}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {Array.isArray(data.sections?.bear_case) && (
                <section
                  className="rounded-md border border-rose-300/60 bg-rose-50/60 dark:bg-rose-950/30 p-2.5"
                  data-testid="exec-summary-section-bear"
                >
                  <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1">
                    <TrendingDown className="w-3.5 h-3.5" /> Bear Case
                  </h3>
                  <ul className="space-y-1 text-xs font-bold">
                    {data.sections.bear_case.map((b, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-rose-600 dark:text-rose-400 mt-1">▼</span>
                        <span className="leading-snug">{b}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {/* What changes our mind */}
            {Array.isArray(data.sections?.what_changes_our_mind) && (
              <section
                className="rounded-md border border-violet-300/60 bg-violet-50/60 dark:bg-violet-950/30 p-2.5"
                data-testid="exec-summary-section-changes"
              >
                <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-violet-700 dark:text-violet-300 mb-1.5 flex items-center gap-1">
                  <Target className="w-3.5 h-3.5" /> What Changes Our Mind
                </h3>
                <ul className="space-y-1 text-xs font-bold">
                  {data.sections.what_changes_our_mind.map((b, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="text-violet-600 dark:text-violet-400 mt-1">→</span>
                      <span className="leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Disclaimer */}
            <p
              className="text-[11px] font-bold italic bg-yellow-200 dark:bg-yellow-300 text-amber-900 px-2.5 py-1.5 rounded leading-relaxed border-2 border-red-400 dark:border-red-600"
              data-testid="exec-summary-disclaimer"
            >
              DiviDrip's Executive Summary is an educational research starter, not financial advice. Always do your own research and consult a licensed advisor before allocating capital.
            </p>

            {data.data_source === 'fallback' && (
              <p className="text-[10px] text-muted-foreground text-center">
                LLM service unavailable — showing rule-based fallback brief.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Popup modal variant (used on the standalone /triangle/:ticker page). */
export function ExecutiveSummaryModal({ ticker, open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="executive-summary-modal-backdrop"
    >
      <div className="bg-background border rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <ExecutiveSummaryBrief ticker={ticker} onClose={onClose} variant="modal" />
      </div>
    </div>
  );
}

/** Full-tab takeover variant (used inside the Stock Modal's Triangle tab). */
export function ExecutiveSummaryTab({ ticker, open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="absolute inset-0 z-30 bg-background overflow-y-auto"
      data-testid="executive-summary-tab-overlay"
    >
      <ExecutiveSummaryBrief ticker={ticker} onClose={onClose} variant="tab" />
    </div>
  );
}
